import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, ArrowLeft, Loader2, AlertCircle, CheckCircle2, MapPin } from 'lucide-react';
import logoUrbnet from '@/assets/images/UrbNet2LOGO.png';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { AppConfig } from '@/datasources/AppConfig';
import { OUVIDORIA_ENDPOINTS } from '@/config';

type ModoCadastro = 'self' | 'admin';

interface EnderecoOuvidoria {
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  pais: string;
  ibge: string;
  latitude?: number;
  longitude?: number;
}

interface DadosCNPJ {
  nome: string;
  fantasia: string;
  razao_social: string;
  numero: string;
  cnpj: string;
  situacao: string;
  natureza_juridica: string;
  atividade_principal: Array<{ code: string; text: string }>;
  cep: string;
}

interface OuvidoriaData {
  dadosCNPJ: DadosCNPJ;
  endereco: EnderecoOuvidoria;
  municipio: { id: string; nome: string; municipioLong: number; codigoIBGE: string };
  cnpj: string;
  geral: boolean;
}

interface BrasilApiCNPJ {
  erro?: string;
  message?: string;
  razao_social?: string;
  nome_fantasia?: string;
  numero?: string;
  logradouro?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  descricao_situacao_cadastral?: string;
  natureza_juridica?: string;
  cnae_fiscal?: number;
  cnae_fiscal_descricao?: string;
  cep?: string;
}

// Busca os dados do CNPJ na BrasilAPI.
// A BrasilAPI suporta CORS diretamente, então tentamos a chamada direta primeiro.
// Caso falhe (rede/instabilidade), usamos proxies CORS como fallback.
const fetchDadosCNPJ = async (cnpjLimpo: string): Promise<BrasilApiCNPJ> => {
  const brasilApiUrl = `https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`;

  // 1) Tentativa direta (BrasilAPI envia cabeçalhos CORS)
  try {
    const direct = await fetch(brasilApiUrl);
    if (direct.ok) {
      return await direct.json();
    }
    // 404 etc.: a BrasilAPI retorna corpo com mensagem; repassa para tratamento
    if (direct.status === 404) {
      return { erro: 'CNPJ não encontrado' };
    }
  } catch {
    // Ignora e tenta os proxies de fallback
  }

  // 2) Fallback via proxy CORS com API key
  const proxies: Array<{ url: string; parse: (raw: string) => unknown }> = [
    {
      url: `https://corsproxy.io/?key=${import.meta.env.VITE_CORSPROXY_KEY}&url=${encodeURIComponent(brasilApiUrl)}`,
      parse: (raw) => JSON.parse(raw),
    },
  ];

  for (const proxy of proxies) {
    try {
      const response = await fetch(proxy.url);
      if (!response.ok) continue;
      const raw = await response.text();
      const parsed = proxy.parse(raw) as BrasilApiCNPJ;
      if (parsed) return parsed;
    } catch {
      // tenta o próximo proxy
    }
  }

  throw new Error('Não foi possível conectar à API de CNPJ. Tente novamente mais tarde.');
};

const CONVITE_TOKEN = import.meta.env.VITE_CONVITE_OUVIDORIA_TOKEN as string | undefined;
const SESSION_KEY = 'conviteOuvidoriaAccess';

const HMAC_KEY = 'urbnet-ouvidoria::convite-key::v1';

async function verifyConviteToken(urlToken: string, plainToken: string): Promise<boolean> {
  try {
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(HMAC_KEY),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(plainToken));
    const expected = btoa(String.fromCharCode(...new Uint8Array(signature)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return urlToken === expected;
  } catch {
    return false;
  }
}

export default function CriarOuvidoriaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const modoCadastro: ModoCadastro = (location.state as { modoCadastro?: ModoCadastro } | null)?.modoCadastro === 'admin' ? 'admin' : 'self';
  const isAdmin = modoCadastro === 'admin';

  // Proteção: em modo self, exige token via URL ou sessionStorage
  useEffect(() => {
    if (isAdmin) return;
    const tokenUrl = searchParams.get('token');
    const validar = async () => {
      if (tokenUrl) {
        // Token na URL: sempre valida via HMAC, nunca usa sessionStorage como atalho
        if (CONVITE_TOKEN && await verifyConviteToken(tokenUrl, CONVITE_TOKEN)) {
          sessionStorage.setItem(SESSION_KEY, '1');
          setIsTokenValidated(true);
          return;
        }
        // Token inválido: nega e limpa qualquer acesso anterior
        sessionStorage.removeItem(SESSION_KEY);
        navigate('/', { replace: true });
        return;
      }
      // Sem token na URL: permite apenas se vier de navegação interna do fluxo
      if (sessionStorage.getItem(SESSION_KEY) === '1') {
        setIsTokenValidated(true);
        return;
      }
      navigate('/', { replace: true });
    };
    validar();
  }, [isAdmin, searchParams, navigate]);
  const [step, setStep] = useState<'municipio' | 'cnpj'>(isAdmin ? 'cnpj' : 'municipio');
  const [municipio, setMunicipio] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ouvidoriaData, setOuvidoriaData] = useState<OuvidoriaData | null>(null);
  const [dadosCNPJ, setDadosCNPJ] = useState<DadosCNPJ | null>(null);
  const [endereco, setEndereco] = useState<EnderecoOuvidoria | null>(null);
  const [municipioValidado, setMunicipioValidado] = useState<{ id: string; nome: string; municipioLong: number; codigoIBGE: string } | null>(null);
  const [isTokenValidated, setIsTokenValidated] = useState(false);
  const [tokenId, setTokenId] = useState<string | null>(null);
  const tokenGeradoRef = useRef(false);

  // Gera um tokenId da API quando o acesso é validado em modo self (ouvidoria geral).
  // O tokenId é usado posteriormente no fluxo de criação para validar a origem do cadastro.
  useEffect(() => {
    if (!isTokenValidated || tokenGeradoRef.current) return;
    tokenGeradoRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const dataSource = new RemoteDataSource(Settings.urlBase);
        const result = await dataSource.post<{ id: string }>({
          endpoint: OUVIDORIA_ENDPOINTS.gerarTokenOuvidoriaGeral,
          body: {},
          requiredToken: false,
          headers: { 'accept': 'application/vnd.data.full.v1+json' },
        });
        if (cancelled) return;
        if ('failure' in result) {
          console.warn('Falha ao gerar tokenId:', result.failure.message);
          return;
        }
        const id = (result.success.body as { id?: string })?.id;
        if (id) setTokenId(id);
      } catch (err) {
        console.warn('Erro ao gerar tokenId:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isTokenValidated]);

  // Em modo admin (gestor j\u00e1 logado) reaproveita o munic\u00edpio j\u00e1 carregado em AppConfig,
  // buscando seus dados completos (nome, codigoIBGE) na API de implantados para que a
  // valida\u00e7\u00e3o de CNPJ vs munic\u00edpio funcione normalmente.
  useEffect(() => {
    if (!isAdmin || municipioValidado) return;
    if (!AppConfig.municipioId) return;

    let cancelled = false;
    (async () => {
      try {
        const dataSource = new RemoteDataSource(Settings.urlBase);
        const result = await dataSource.get<Record<string, unknown>[]>({
          endpoint: OUVIDORIA_ENDPOINTS.municipiosImplantados,
          headers: { Accept: 'application/vnd.data.full.v1+json' },
          requiredToken: false,
          forceRefresh: true,
        });
        if ('failure' in result) return;
        const lista = result.success.body as Record<string, unknown>[];
        const atual = lista.find((m) => {
          const mu = m.municipio as Record<string, unknown> | undefined;
          return String(mu?.guid || '') === AppConfig.municipioId;
        });
        if (!atual || cancelled) return;
        const mu = atual.municipio as Record<string, unknown>;
        setMunicipioValidado({
          id: String(mu.guid || ''),
          nome: String(mu.nome || ''),
          municipioLong: Number(mu.id || AppConfig.municipioLong || 0),
          codigoIBGE: String(mu.codigoIBGE || ''),
        });
      } catch (err) {
        // Error loading municipio data
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, municipioValidado]);

  // Função para geocodificar endereço usando Nominatim (OpenStreetMap) - suporta CORS
  const geocodificarEndereco = async (endereco: EnderecoOuvidoria): Promise<{ latitude: number; longitude: number } | null> => {
    try {
      // Tenta primeiro com o endereço completo
      let query = '';
      if (endereco.logradouro && endereco.numero) {
        query = `${endereco.logradouro}, ${endereco.numero}, ${endereco.bairro || ''}, ${endereco.cidade}, ${endereco.estado}, ${endereco.pais || 'Brasil'}`.replace(/\s+/g, ' ').trim();
      } else {
        query = `${endereco.cidade}, ${endereco.estado}, ${endereco.pais || 'Brasil'}`;
      }

      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`, {
        headers: {
          'User-Agent': 'UrbNetOuvidoria/1.0',
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
        };
      }

      // Fallback: apenas cidade e estado
      const queryFallback = `${endereco.cidade}, ${endereco.estado}, ${endereco.pais || 'Brasil'}`;
      const response2 = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(queryFallback)}&limit=5`, {
        headers: {
          'User-Agent': 'UrbNetOuvidoria/1.0',
        },
      });

      if (!response2.ok) {
        return null;
      }

      const data2 = await response2.json();

      if (data2 && data2.length > 0) {
        interface NominatimResult {
          lat: string;
          lon: string;
          importance?: number;
        }

        const bestResult = data2.reduce((best: NominatimResult, current: NominatimResult) => {
          return (current.importance || 0) > (best.importance || 0) ? current : best;
        }, data2[0] as NominatimResult);

        return {
          latitude: parseFloat(bestResult.lat),
          longitude: parseFloat(bestResult.lon),
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Normaliza strings para comparação tolerante: remove acentos, pontuação e espaços extras.
  const normalizarMunicipio = (valor: string | undefined | null) =>
    (valor || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numbers = e.target.value.replace(/\D/g, '');
    setCnpj(formatCNPJ(numbers));
    setError(null);
  };

  const handleMunicipioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMunicipio(e.target.value);
    setError(null);
  };

  const validarMunicipio = async () => {
    if (!municipio.trim()) {
      setError('Nome do município é obrigatório');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      
      const result = await dataSource.get<Record<string, unknown>[]>({
        endpoint: OUVIDORIA_ENDPOINTS.municipiosImplantados,
        headers: { 'Accept': 'application/vnd.data.full.v1+json' },
        forceRefresh: true,
      });

      if ('failure' in result) {
        throw new Error(result.failure.messages?.join(', ') || 'Erro ao buscar municípios implantados');
      }

      const municipios = result.success.body as Record<string, unknown>[];

      const municipioEncontrado = municipios.find(
        (m: Record<string, unknown>) => {
          // The municipality data is nested inside the 'municipio' property
          const municipioData = m.municipio as Record<string, unknown>;
          const nome = municipioData?.nome as string;
          const match = nome?.toLowerCase() === municipio.toLowerCase().trim();
          return match;
        }
      );

      if (!municipioEncontrado) {
        setError('Este município não tem o sistema UrbNet implantado');
        setIsLoading(false);
        return;
      }

      const municipioData = municipioEncontrado.municipio as Record<string, unknown>;
      setMunicipioValidado({
        id: String(municipioData?.guid || ''), // municipioId vem do campo guid dentro de municipio
        nome: String(municipioData?.nome || ''),
        municipioLong: Number(municipioData?.id || 0), // municipioLong vem do campo id dentro de municipio
        codigoIBGE: String(municipioData?.codigoIBGE || ''), // codigoIBGE do municipio
      });

      toast({
        title: 'Município validado',
        description: `O município ${municipioData?.nome} está cadastrado no sistema.`,
      });

      setStep('cnpj');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocorreu um erro ao validar o município');
      toast({
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Ocorreu um erro ao validar o município',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const buscarCNPJ = async () => {
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    
    if (cnpjLimpo.length !== 14) {
      setError('CNPJ deve conter 14 dígitos');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // BrasilAPI suporta CORS diretamente; usamos proxies apenas como fallback
      const receitaData = await fetchDadosCNPJ(cnpjLimpo);

      if (receitaData.erro) {
        throw new Error(receitaData.erro || 'CNPJ não encontrado');
      }

      // Extract data - BrasilAPI structure
      const novoDadosCNPJ: DadosCNPJ = {
        nome: receitaData.razao_social || '',
        fantasia: receitaData.nome_fantasia || '',
        razao_social: receitaData.razao_social || '',
        numero: receitaData.numero || '',
        cnpj: cnpj,
        situacao: receitaData.descricao_situacao_cadastral || '',
        natureza_juridica: receitaData.natureza_juridica || '',
        atividade_principal: [
          {
            code: receitaData.cnae_fiscal?.toString() || '',
            text: receitaData.cnae_fiscal_descricao || '',
          }
        ],
        cep: receitaData.cep || '',
      };

      setDadosCNPJ(novoDadosCNPJ);

      // Check if CNPJ is active
      const ativa = receitaData.descricao_situacao_cadastral === 'ATIVA';
      if (!ativa) {
        setError('CNPJ inativo');
        setIsLoading(false);
        return;
      }

      // Validate natureza juridica
      const naturezaJuridicaValida =
        novoDadosCNPJ.natureza_juridica.includes('Município') ||
        novoDadosCNPJ.natureza_juridica.includes('Associação Privada');

      if (!naturezaJuridicaValida) {
        setError('CNPJ inválido para uma ouvidoria');
        setIsLoading(false);
        return;
      }

      // Validate atividade principal (CNAE)
      const cnaePrincipal = receitaData.cnae_fiscal || 0;
      const cnaeDescricao = receitaData.cnae_fiscal_descricao || '';
      const atividadeValida =
        cnaePrincipal === 9430800 || // Atividades de associações de defesa de direitos sociais
        cnaePrincipal === 8411600 || // Administração pública em geral
        cnaeDescricao.toLowerCase().includes('defesa de direitos sociais') ||
        cnaeDescricao.toLowerCase().includes('administração pública');

      if (!atividadeValida) {
        setError('Atividade principal do CNPJ inválida');
        setIsLoading(false);
        return;
      }

      // Valida que o município do CNPJ bate com o município validado
      const municipioReceita = receitaData.municipio || '';
      const nomeCanonico = municipioValidado?.nome || municipio;
      const municipioBate =
        !!municipioReceita &&
        normalizarMunicipio(municipioReceita) === normalizarMunicipio(nomeCanonico);

      if (!municipioBate) {
        const msg = `O município do CNPJ (${municipioReceita || 'não informado'}) não corresponde ao município informado (${nomeCanonico}). Não é permitido criar a ouvidoria.`;
        setError(msg);
        toast({
          title: 'Município divergente',
          description: msg,
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      // Extract CEP and clean it
      const cepLimpo = (novoDadosCNPJ.cep as string)
        .replace(/\./g, '')
        .replace(/-/g, '');

      // Create address object com defaults do CNPJ
      let novoEndereco: EnderecoOuvidoria = {
        logradouro: receitaData.logradouro || '',
        numero: receitaData.numero || '',
        bairro: receitaData.bairro || '',
        cidade: receitaData.municipio || '',
        estado: receitaData.uf || '',
        cep: cepLimpo,
        pais: 'Brasil',
        ibge: '',
      };

      // Call ViaCEP API (não bloqueia o fluxo se falhar - usa dados do CNPJ como fallback)
      try {
        const cepResponse = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        if (cepResponse.ok) {
          const cepData = await cepResponse.json();
          if (!cepData.erro) {
            novoEndereco = {
              logradouro: cepData.logradouro || novoEndereco.logradouro,
              numero: novoDadosCNPJ.numero,
              bairro: cepData.bairro || novoEndereco.bairro,
              cidade: cepData.localidade || novoEndereco.cidade,
              estado: cepData.uf || novoEndereco.estado,
              cep: cepLimpo,
              pais: 'Brasil',
              ibge: cepData.ibge || '',
            };
          }
        }
      } catch (cepErr) {
        // Fallback: mantém endereço montado com dados do CNPJ
        console.warn('ViaCEP indisponível, usando dados do CNPJ:', cepErr);
      }

      // Geocodificar endereço (não bloqueia o fluxo se falhar)
      try {
        const coords = await geocodificarEndereco(novoEndereco);
        if (coords) {
          novoEndereco.latitude = coords.latitude;
          novoEndereco.longitude = coords.longitude;
        }
      } catch (geoErr) {
        console.warn('Geocodificação indisponível:', geoErr);
      }

      setEndereco(novoEndereco);

      // Verifica se natureza jurídica é Associação Privada para não permitir ouvidoria geral
      const isAssociacaoPrivada = novoDadosCNPJ.natureza_juridica.includes('Associação Privada');
      
      // Navigate to edit ouvidoria page with data
      setTimeout(() => {
        navigate('/editar-ouvidoria', {
          state: {
            dadosCNPJ: novoDadosCNPJ,
            endereco: novoEndereco,
            municipio: municipioValidado,
            cnpj: cnpj,
            // Regras para ouvidoria geral:
            // - Em modo admin (gestor da geral cadastrando setorial) força geral=false
            // - Em modo self com Associação Privada força geral=false
            // - Em modo self com token validado força geral=true (ouvidoria-geral)
            // - Em modo self com Município permite geral=true
            geral: isTokenValidated || (!isAdmin && !isAssociacaoPrivada),
            modoCadastro,
            isAssociacaoPrivada,
            tokenId,
          },
        });
      }, 500);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocorreu um erro ao buscar o CNPJ');
      toast({
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Ocorreu um erro ao buscar o CNPJ',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 'municipio') {
      await validarMunicipio();
    } else {
      await buscarCNPJ();
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-dark via-primary to-secondary relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-2xl" />
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTAgMGg0MHY0MEgweiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-16 object-contain"
            />
          </div>

          {/* Main Content */}
          <div className="space-y-8 max-w-md">
            <div>
              <h2 className="font-heading text-4xl font-bold leading-tight">
                Crie sua ouvidoria
                <br />
                em minutos
              </h2>
              <p className="mt-6 text-lg text-white/80 leading-relaxed">
                Cadastre sua ouvidoria usando o CNPJ. Validaremos automaticamente
                os dados e carregaremos as informações da Receita Federal.
              </p>
            </div>

            {/* Info */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-white/80">Validação automática</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-white/80">Busca automática de endereço</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-white/80">Apenas para Municípios e Associações Privadas</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-sm text-white/50">
            <p>© 2024 UrbNet. Todos os direitos reservados.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-14 object-contain"
            />
          </div>

          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>

          {/* Header */}
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl font-bold">
              {isAdmin ? 'Nova Ouvidoria Setorial' : 'Criar Ouvidoria'}
            </h2>
            <p className="text-muted-foreground mt-2">
              {step === 'municipio'
                ? 'Informe o nome do município'
                : 'Informe o CNPJ da instituição'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {step === 'municipio' ? (
              /* Municipio Field */
              <div className="space-y-2">
                <Label htmlFor="municipio" className="text-sm font-medium">
                  Nome do Município
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="municipio"
                    placeholder="Ex: São Paulo"
                    value={municipio}
                    onChange={handleMunicipioChange}
                    className="pl-12 h-14 text-base"
                    disabled={isLoading}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* CNPJ Field */}
                <div className="space-y-2">
                  <Label htmlFor="cnpj" className="text-sm font-medium">
                    CNPJ
                  </Label>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="cnpj"
                      placeholder="00.000.000/0000-00"
                      value={cnpj}
                      onChange={handleCNPJChange}
                      maxLength={18}
                      className="pl-12 h-14 text-base"
                      disabled={isLoading}
                    />
                  </div>
                  {municipioValidado && (
                    <p className="text-sm text-muted-foreground">
                      Município: {municipioValidado.nome}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={
                step === 'municipio' 
                  ? !municipio.trim() || isLoading
                  : cnpj.replace(/\D/g, '').length !== 14 || isLoading
              }
              className="w-full h-14 text-base font-medium gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {step === 'municipio' ? 'Validando...' : 'Validando CNPJ...'}
                </>
              ) : (
                step === 'municipio' ? 'Validar Município' : 'Validar CNPJ'
              )}
            </Button>

            {/* Back to Municipio Button (only shown in CNPJ step) */}
            {step === 'cnpj' && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStep('municipio');
                  setCnpj('');
                  setError(null);
                }}
                disabled={isLoading}
                className="w-full"
              >
                Voltar para Município
              </Button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
